import React, { useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import { NextSeo } from "next-seo";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import connectToDb from "../../../../utils/mongodb";
import { ObjectId } from "mongodb";
import { Button } from "@jammable/ui-core";
import { FaApple, FaGooglePlay, FaDiscord, FaPlay } from "react-icons/fa";
import Link from "next/link";
import { track } from "../../../../utils/analytics";
import sharp from "sharp";

import cdCaseImg from "../../../../assets/cdCase.png";

// Extract dominant color from image URL using sharp
async function extractDominantColor(imageUrl) {
  if (!imageUrl) return null;
  try {
    const response = await fetch(imageUrl);
    const buffer = Buffer.from(await response.arrayBuffer());
    const { dominant } = await sharp(buffer).stats();
    return `${dominant.r},${dominant.g},${dominant.b}`;
  } catch {
    return null;
  }
}

dayjs.extend(relativeTime);

const JammableLogo = ({ width = 80, className }) => (
  <svg
    width={width}
    viewBox="0 0 739 468"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M575.988 5.10379C580.61 4.30385 585.341 5.7162 588.767 8.92027L619.404 37.5708L619.928 38.0871C622.483 40.7242 623.987 44.217 624.135 47.9036L628.89 166.127L682.042 156.84L682.912 156.714C687.261 156.214 691.629 157.643 694.847 160.654L725.703 189.518C728.741 192.36 730.459 196.339 730.445 200.5L730.254 258.17C730.24 262.324 728.519 266.126 725.712 268.854L725.568 300.276L726.132 300.8C729.179 303.637 730.908 307.614 730.902 311.777L730.824 369.346C730.815 376.622 725.575 382.836 718.407 384.077L625.614 400.131C625.084 400.285 624.543 400.415 623.989 400.511L527.055 417.285C522.232 418.119 517.305 416.544 513.862 413.065L511.428 410.607C500.025 418.923 486.868 424.239 472.455 426.734L355.528 446.969C354.712 447.11 353.892 447.177 353.076 447.183C352.304 447.453 351.503 447.663 350.676 447.807L289.639 458.372C285.017 459.172 280.288 457.759 276.861 454.555L245.147 424.899C241.455 421.446 239.754 416.364 240.625 411.385L261.334 293.066L158.852 310.801C154.484 311.557 150.021 310.335 146.651 307.494C135.672 315.033 122.952 319.776 109.187 322.158C91.3747 325.241 72.0178 324.292 56.1055 312.862L55.5153 312.44C54.9934 312.065 54.4956 311.656 54.0261 311.217C44.8249 302.614 34.965 294.55 26.2341 285.481C11.6428 270.709 6.95872 250.481 7.00084 230.392C7.00085 230.385 7.00008 230.377 7.0001 230.37L7.08322 206.985C7.41612 200.014 12.5465 194.156 19.4825 192.955L59.0761 186.102L59.3325 107.064L59.3499 106.385C59.6852 99.4162 64.8156 93.5617 71.7494 92.3611L93.0422 88.6729C93.4828 88.5552 93.9321 88.4573 94.3891 88.379L134.423 81.5217L135.289 81.3993C138.259 81.0674 141.232 81.639 143.843 82.9935C146.036 80.5779 149.008 78.8851 152.375 78.3023L225.663 65.6198L226.531 65.4956C230.87 65.0035 235.227 66.431 238.438 69.4327L239.89 70.7901C241.713 65.914 246.008 62.2078 251.359 61.2809L323.861 48.7199L324.727 48.5957C326.731 48.368 328.739 48.5495 330.636 49.1068C332.445 47.7971 334.561 46.8839 336.859 46.486L407.621 34.2395L408.489 34.1152C410.31 33.9087 412.134 34.0438 413.875 34.4883C415.589 33.3301 417.563 32.5142 419.706 32.1441L492.26 19.6106L493.127 19.4883C495.107 19.2642 497.089 19.4426 498.965 19.9852C500.777 18.6711 502.896 17.7536 505.2 17.3549L575.988 5.10379ZM507.754 32.1052L505.539 44.3918L494.809 34.3629L422.255 46.8964L421.667 59.7263L410.175 48.9898L339.414 61.2363L337.197 73.5339L326.417 63.4701L253.915 76.0311L252.67 103.225L228.217 80.3701L154.929 93.0526L151.913 110.274L136.952 96.2765L96.9178 103.134L96.9163 103.196L74.3036 107.111L74.0084 198.712L22.0368 207.706L21.9711 230.414L21.9714 230.423C21.9334 248.545 26.2023 264.243 37.0189 275.099L37.053 275.134L37.087 275.168C38.18 276.245 39.3254 277.232 40.502 278.145C44.4397 281.79 50.1309 287.094 54.9014 291.546C57.3536 293.835 59.5597 295.896 61.1514 297.383L64.2511 300.281L64.8394 300.704C75.8004 308.577 89.8833 310.06 105.153 307.653L106.634 307.408C122.961 304.582 136.755 298.124 147.269 287.609L156.3 296.051L279.751 274.69L255.372 413.965L287.086 443.621L348.124 433.056L348.615 428.136L352.976 432.219L469.902 411.984C486.211 409.161 500.182 402.237 511.317 391.032L511.335 391.015L511.351 390.998C511.639 390.705 511.923 390.407 512.204 390.11L524.503 402.534L621.436 385.761L621.435 385.664L715.854 369.326L715.932 311.757L710.57 306.764L710.789 258.897L715.284 258.121L715.476 200.45L684.62 171.586L620.2 182.844L614.622 183.808L609.179 48.5046L578.542 19.8541L507.754 32.1052Z"
      fill="white"
    />
    <path
      d="M609.178 48.5047L614.622 183.809L620.199 182.844L684.619 171.586L715.475 200.45L715.283 258.121L710.788 258.897L710.569 306.764L715.931 311.757L715.853 369.326L621.434 385.664L621.435 385.761L524.502 402.534L512.203 390.11C511.922 390.407 511.638 390.705 511.35 390.998L511.334 391.015L511.316 391.032C500.181 402.237 486.21 409.161 469.901 411.984L352.975 432.219L348.614 428.136L348.123 433.056L287.086 443.621L255.371 413.965L279.75 274.69L217.334 285.488L156.299 296.051L147.268 287.609C136.754 298.124 122.961 304.582 106.634 307.408C90.7947 310.149 76.1532 308.832 64.8386 300.704L64.2502 300.282L61.1506 297.383C59.5587 295.896 57.3529 293.835 54.9005 291.547C50.1298 287.094 44.4389 281.79 40.5012 278.145C39.3244 277.232 38.1792 276.246 37.0862 275.168L37.0521 275.134L37.018 275.099C26.2015 264.243 21.9325 248.545 21.9706 230.423L21.9703 230.414L22.0359 207.706L74.0075 198.712L74.3028 107.111L96.9154 103.196L96.917 103.134L136.951 96.2765L151.912 110.274L154.928 93.0526L228.216 80.3702L252.67 103.226L253.914 76.0312L326.416 63.4702L337.197 73.534L339.413 61.2363L410.174 48.9898L421.666 59.7263L422.254 46.8964L494.808 34.363L505.538 44.3919L507.753 32.1052L578.542 19.8541L609.178 48.5047Z"
      fill="transparent"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M381.738 390.143L333.375 398.513L330.831 375.905L316.952 378.306L314.607 401.76L267.482 409.916L295.413 250.337L355.429 239.951L381.738 390.143ZM323.399 304.651C323.222 305.673 322.963 309.294 322.623 315.513C322.352 321.72 322.104 326.224 321.881 329.025L320.475 343.074L327.012 341.942L325.489 328.399C325.232 325.682 324.93 321.274 324.583 315.175C324.235 309.076 323.978 305.544 323.812 304.581L323.399 304.651Z"
      fill="white"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M444.282 224.681C458.031 222.302 468.907 223.785 476.912 229.126C484.917 234.467 488.92 243.687 488.921 256.786C488.958 266.408 487.378 274.259 484.181 280.334C480.972 286.341 477.293 290.447 473.143 292.652L473.179 292.858C478.734 293.454 483.643 296.252 487.906 301.249C492.169 306.247 494.286 314.378 494.253 325.642C494.192 340.025 489.494 351.953 480.159 361.428C470.813 370.834 459.024 376.768 444.793 379.231L388.592 388.957L389.113 234.228L444.282 224.681ZM444.567 317.777C443.63 316.452 442.163 315.961 440.17 316.306L435.014 317.2L434.927 344.935L440.29 344.007C442.215 343.674 443.638 342.685 444.557 341.039C445.544 339.381 446.052 335.363 446.081 328.986C446.065 322.758 445.561 319.021 444.567 317.777ZM443.616 264.412C442.703 263.225 441.215 262.81 439.153 263.167L435.234 263.846L435.047 289.156L439.068 288.459C441.13 288.102 442.599 287.175 443.473 285.678C444.403 284.101 444.897 280.617 444.956 275.227C444.976 269.205 444.529 265.6 443.616 264.412Z"
      fill="white"
    />
    <path
      d="M553.056 317.165L587.189 311.257L587.045 354.615L505.167 368.783L505.688 214.056L553.432 205.794L553.056 317.165Z"
      fill="white"
    />
    <path
      d="M680.896 227.069L641.917 233.814L641.92 245.496L676.362 239.536L676.164 282.585L641.722 288.545L641.702 301.931L681.506 295.043L681.448 338.279L595.962 353.072L596.483 198.345L681.04 183.712L680.896 227.069Z"
      fill="white"
    />
    <path
      d="M132.999 207.546C132.964 227.802 128.502 243.338 119.611 254.152C110.72 264.966 98.0245 271.8 81.5256 274.655C65.0952 277.498 52.9103 275.005 44.9704 267.174C37.0874 259.263 33.1629 247.021 33.1977 230.446L33.2361 217.163L78.8142 209.277L78.7766 228.082C78.7315 231.913 78.8672 234.332 79.1831 235.34C79.4991 236.347 80.3782 236.726 81.8213 236.476C83.1962 236.238 84.0975 235.515 84.5259 234.308C84.9543 233.101 85.1615 230.41 85.1471 226.235L85.4993 116.569L133.348 108.288L132.999 207.546Z"
      fill="white"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M250.95 242.577L202.587 250.944L200.043 228.337L186.163 230.739L183.819 254.193L136.694 262.347L164.625 102.769L224.641 92.3844L250.95 242.577ZM192.611 157.085C192.433 158.107 192.174 161.728 191.834 167.946C191.563 174.153 191.316 178.656 191.092 181.456L189.686 195.506L196.223 194.374L194.701 180.832C194.443 178.115 194.142 173.706 193.794 167.606C193.447 161.507 193.19 157.975 193.023 157.012L192.611 157.085Z"
      fill="white"
    />
    <path
      d="M412.793 214.569L365.255 222.796L364.539 157.284C364.577 155.862 364.533 151.726 364.41 144.879C364.356 138.021 364.353 133.703 364.399 131.925L364.192 131.961C364.077 133.751 363.771 138.123 363.276 145.076C362.768 151.961 362.427 156.127 362.253 157.573L355.149 224.545L312.972 231.843L306.582 166.676C306.413 165.289 306.135 161.23 305.749 154.499C305.432 147.757 305.24 143.789 305.175 142.597L304.866 142.651C304.931 143.844 304.813 147.866 304.511 154.714C304.266 161.483 304.166 165.607 304.21 167.087L302.867 233.593L257.598 241.425L264.718 85.5549L322.877 75.4905L333.602 137.466C333.909 138.829 334.189 140.657 334.444 142.949C334.768 145.229 334.946 147.075 334.978 148.486L335.596 148.379C335.77 146.933 335.971 145.022 336.197 142.647C336.492 140.259 336.83 138.324 337.21 136.842L349.07 70.9594L406.609 61.0019L412.793 214.569Z"
      fill="white"
    />
    <path
      d="M581.133 185.438L533.595 193.664L532.88 128.153C532.917 126.731 532.873 122.596 532.75 115.749C532.696 108.891 532.693 104.572 532.739 102.793L532.534 102.83C532.419 104.62 532.112 108.992 531.616 115.945C531.108 122.83 530.767 126.995 530.593 128.442L523.489 195.414L481.313 202.712L474.922 137.545C474.753 136.158 474.475 132.099 474.089 125.368C473.772 118.626 473.582 114.658 473.517 113.466L473.207 113.52C473.271 114.713 473.155 118.734 472.853 125.583C472.608 132.352 472.506 136.476 472.55 137.956L471.207 204.461L425.938 212.295L433.058 56.4238L491.218 46.3594L501.942 108.335C502.249 109.698 502.529 111.525 502.784 113.818C503.108 116.098 503.286 117.944 503.318 119.355L503.936 119.248C504.11 117.802 504.311 115.891 504.537 113.516C504.832 111.128 505.17 109.193 505.551 107.711L517.41 41.8283L574.951 31.8707L581.133 185.438Z"
      fill="white"
    />
  </svg>
);

const backgroundLayoutParams = [
  // Top half (vocals)
  { size: 350, top: "-10%", left: "0%", transform: "rotate(-20deg)" },
  { size: 200, top: "10%", left: "70%", transform: "rotate(15deg)" },
  { size: 300, top: "25%", left: "-5%", transform: "rotate(5deg)" },
  { size: 220, top: "30%", left: "80%", transform: "rotate(-10deg)" },

  // Bottom half (instrumental)
  { size: 350, top: "35%", left: "-10%", transform: "rotate(20deg)" },
  { size: 250, top: "60%", left: "75%", transform: "rotate(-15deg)" },
  { size: 200, top: "75%", left: "-30%", transform: "rotate(-5deg)" },
  { size: 380, top: "40%", left: "65%", transform: "rotate(10deg)" },
];

// Simple seeded RNG (mulberry32) for deterministic randomness per mashup
function mulberry32(seed) {
  let t = seed >>> 0;
  return function () {
    t |= 0;
    t = (t + 0x6d2b79f5) | 0;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

function createSeededRngFromString(key) {
  let seed = 2166136261;
  for (let i = 0; i < key.length; i++) {
    seed ^= key.charCodeAt(i);
    seed = Math.imul(seed, 16777619);
  }
  return mulberry32(seed);
}

function clamp(num, min, max) {
  return Math.max(min, Math.min(max, num));
}

function randomizeLayout(baseLayout, seedKey) {
  const rng = createSeededRngFromString(String(seedKey || "default"));
  return baseLayout.map((item) => {
    const sizeFactor = 0.85 + rng() * 0.4; // 0.85x to 1.25x
    const size = Math.round(item.size * sizeFactor);

    const parsePercent = (str) => parseFloat(String(str).replace("%", ""));
    const toPercent = (num) => `${num}%`;

    const topBase = parsePercent(item.top);
    const leftBase = parsePercent(item.left);

    const topOffset = -15 + rng() * 30; // -15% to +15%
    const leftOffset = -20 + rng() * 40; // -20% to +20%

    const top = toPercent(clamp(topBase + topOffset, -30, 90));
    const left = toPercent(clamp(leftBase + leftOffset, -30, 90));

    const baseAngleMatch = /-?\d+(?:\.\d+)?/.exec(item.transform || "");
    const baseAngle = baseAngleMatch ? parseFloat(baseAngleMatch[0]) : 0;
    const angleOffset = -20 + rng() * 40; // -20deg to +20deg
    const transform = `rotate(${baseAngle + angleOffset}deg)`;

    return { ...item, size, top, left, transform };
  });
}

const MashupPage = ({ mashupData: initialMashupData, ogImageUrl }) => {
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [source, setSource] = useState(null);
  const [explicitLocale, setExplicitLocale] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const src =
      params.get("source") ||
      params.get("utm_source") ||
      params.get("utm_medium") ||
      params.get("utm_campaign") ||
      params.get("utm_content") ||
      params.get("from") ||
      params.get("ref") ||
      params.get("q");

    const langParam =
      params.get("lang") ||
      params.get("locale") ||
      params.get("language") ||
      params.get("hl");

    setSource(src);

    if (langParam) {
      const key = langParam.toLowerCase();
      const LANG_PARAM_TO_LOCALE = {
        en: "en",
        fr: "fr",
        pt: "pt",
        es: "es",
        ru: "ru",
        sk: "sk",
      };
      const resolved = LANG_PARAM_TO_LOCALE[key];
      if (resolved) setExplicitLocale(resolved);
    }
  }, []);

  const locale = explicitLocale || "en";

  const handleDiscordClick = async () => {
    await track("discord_invite_click", {
      source: source,
      location: "mashup_page",
      language: locale,
    });
    window.open("https://discord.gg/jammable", "_blank");
  };

  const handleAppStoreClick = async () => {
    await track("app_store_click", {
      source,
      location: "mashup_page",
      language: locale,
    });
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const url = isIOS
      ? "itms-apps://itunes.apple.com/app/id6526492369"
      : "https://apps.apple.com/app/id6526492369";
    window.open(url, "_blank");
  };

  const handlePlayStoreClick = async () => {
    await track("play_store_click", {
      source,
      location: "mashup_page",
      language: locale,
    });
    window.open(
      "https://play.google.com/store/apps/details?id=com.jammable.app",
      "_blank",
    );
  };

  const audioRef = useRef(null);

  useEffect(() => {
    const url = initialMashupData?.mashupRecipe?.previewUrl;
    console.log(url);
    if (!url) return;

    const audio = new Audio(url);
    audioRef.current = audio;

    const handleEnded = () => {
      setIsPlaying(false);
      setProgress(0);
    };

    const handleTimeUpdate = () => {
      if (audio.duration) {
        setProgress((audio.currentTime / audio.duration) * 100);
      }
    };

    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("timeupdate", handleTimeUpdate);

    audio
      .play()
      .then(() => setIsPlaying(true))
      .catch((err) => {
        setIsPlaying(false);
        setError(err?.message || "Autoplay blocked");
      });

    return () => {
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.pause();
      audio.src = "";
      audioRef.current = null;
      setProgress(0);
    };
  }, [initialMashupData?.mashupRecipe?.previewUrl]);

  useEffect(() => {
    if (error) {
      console.warn("Failed to autoplay preview:", error);
    }
  }, [error]);

  const togglePlayback = () => {
    const audio = audioRef.current;
    console.log(audio);
    if (!audio) return;

    if (audio.paused) {
      audio
        .play()
        .then(() => setIsPlaying(true))
        .catch((err) => {
          setIsPlaying(false);
          setError(err?.message || "Playback error");
        });
    } else {
      audio.pause();
      setIsPlaying(false);
    }
  };

  const handleProgressClick = (e) => {
    e.stopPropagation(); // Prevent triggering the play/pause overlay
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * audio.duration;

    audio.currentTime = newTime;
    setProgress(percentage * 100);
  };

  let vocalTrack, instrumentalTrack;
  if (initialMashupData && initialMashupData.tracks) {
    const { mashupRecipe } = initialMashupData;
    const vocalTrackIdObj =
      mashupRecipe.recordingSession?.initialState?.activeVocalTrackId ||
      mashupRecipe?.recordingSession?.alignmentChanges[0]?.alignment
        .vocalTrackId;
    const instrumentalTrackIdObj =
      mashupRecipe.recordingSession?.initialState?.activeInstrumentalTrackId ||
      mashupRecipe?.recordingSession?.alignmentChanges[0]?.alignment
        .instrumentalTrackId;

    const vocalTrackId = vocalTrackIdObj?.$oid || vocalTrackIdObj;
    const instrumentalTrackId =
      instrumentalTrackIdObj?.$oid || instrumentalTrackIdObj;

    vocalTrack = initialMashupData.tracks.find((t) => t._id === vocalTrackId);
    instrumentalTrack = initialMashupData.tracks.find(
      (t) => t._id === instrumentalTrackId,
    );
  }

  const displayData = initialMashupData;
  const displayTracks = { vocal: vocalTrack, instrumental: instrumentalTrack };

  const seedKey =
    initialMashupData?._id?.$oid ||
    initialMashupData?._id ||
    displayData?.mashupRecipe?.title ||
    "default";
  const randomizedLayoutParams = useMemo(
    () => randomizeLayout(backgroundLayoutParams, seedKey),
    [seedKey],
  );

  if (!initialMashupData) {
    // Basic loading/not found state, can be improved
    return (
      <div className="tw-min-h-screen tw-bg-black tw-flex tw-items-center tw-justify-center">
        <div className="tw-text-white">Mashup not found.</div>
      </div>
    );
  }

  const trackComponent = (track, isVocal) => {
    const imageSrc = track?.metadata?.image?.url || cdCaseImg;
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-z-10">
        <div className="tw-relative tw-flex tw-items-center tw-justify-center">
          <Image
            src={imageSrc}
            alt={track?.metadata?.title}
            width={100}
            height={100}
            className="tw-rounded-xl tw-z-10 tw-object-cover"
          />
          {/* spinning cd */}
          <div
            className={`tw-absolute tw-z-1 tw-w-[80px] tw-h-[80px] tw-rounded-full tw-overflow-hidden tw-transition-all tw-duration-500 ${
              isPlaying
                ? "tw-transform tw-translate-x-0"
                : "tw-transform tw-translate-x-0 tw-translate-y-0"
            }`}
            style={{
              transform: isPlaying
                ? isVocal
                  ? "translateY(30px)"
                  : "translateY(-30px)"
                : "translateY(0px)",
            }}
          >
            <Image
              src={imageSrc}
              alt={track?.metadata?.title}
              width={80}
              height={80}
              className={`${
                isPlaying ? "tw-animate-[spin_0.3s_linear_infinite]" : ""
              } tw-object-cover ${
                isPlaying ? "tw-blur-[2px]" : "tw-blur-none"
              } tw-border-2 tw-border-white tw-border-solid tw-transition-all tw-duration-500`}
            />
          </div>
        </div>

        <div className="tw-max-w-[150px]">
          <div className="tw-text-sm tw-text-white/60 tw-font-bold">
            {isVocal ? "Vocal" : "Beat"} of
          </div>
          <div
            style={{
              fontFamily: "Roboto Condensed !important",
              fontWeight: "900",
              fontStyle: "italic",
            }}
            className="tw-italic tw-uppercase tw-text-2xl tw-truncate tw-text-white"
          >
            {track?.metadata?.title}
          </div>
        </div>
      </div>
    );
  };

  const backgroundComponent = (tracks, blurClass = "tw-blur-[150px]") => (
    <div className="tw-absolute tw-top-0 tw-left-0 tw-w-full tw-h-full tw-z-1 md:tw-overflow-hidden">
      {randomizedLayoutParams.map((style, index) => {
        const vocalImg = tracks.vocal?.metadata?.image?.url;
        const instrumentalImg = tracks.instrumental?.metadata?.image?.url;
        let imageUrl;
        if (index < randomizedLayoutParams.length / 2) {
          // Top half, prefer vocal
          imageUrl = vocalImg || instrumentalImg || cdCaseImg;
        } else {
          // Bottom half, prefer instrumental
          imageUrl = instrumentalImg || vocalImg || cdCaseImg;
        }

        return (
          <div
            key={index}
            className="tw-absolute"
            style={{
              top: style.top,
              left: style.left,
              width: `${(style.size / 1440) * 100}vw`,
              height: `${(style.size / 1440) * 100}vw`,
              transform: style.transform,
            }}
          >
            <Image
              src={imageUrl}
              alt=""
              fill
              sizes={`${(style.size / 1440) * 100}vw`}
              className={`${blurClass} tw-filter tw-saturate-150 tw-z-1 tw-object-cover`}
            />
          </div>
        );
      })}
    </div>
  );

  return (
    <div>
      <NextSeo
        title={`${displayData.mashupRecipe?.title || "Mashup"} - Jammable`}
        description={`Listen to ${
          displayData.mashupRecipe?.title || "a mashup"
        } on Jammable - The ultimate mashup creation app`}
        canonical={`https://www.jammable.com/@/${initialMashupData?.user?.username}/post/${initialMashupData?._id}`}
        openGraph={{
          title: `${displayData.mashupRecipe?.title || "Mashup"} - Jammable`,
          description:
            displayData.caption || "Check out this amazing mashup on Jammable",
          type: "music.song",
          images: [
            {
              url:
                ogImageUrl ||
                displayTracks.vocal?.metadata?.image?.url ||
                displayTracks.instrumental?.metadata?.image?.url ||
                (cdCaseImg && cdCaseImg.src),
              width: 1200,
              height: 630,
              alt: displayData.mashupRecipe?.title || "Jammable Mashup",
            },
          ],
        }}
        twitter={{
          cardType: "summary_large_image",
        }}
      />

      <div className="tw-min-h-screen  tw-bg-black  tw-items-center tw-justify-center tw-flex-col-reverse tw-pb-8 md:tw-pb-0 tw-flex md:tw-flex-row tw-w-full">
        <h1 className="tw-sr-only">{displayData.mashupRecipe?.title || "Mashup"} - Jammable</h1>
        {backgroundComponent(displayTracks, "tw-blur-[200px]")}

        <div className="tw-w-full  tw-flex tw-flex-col md:tw-items-end tw-items-center">
          <div className="tw-relative tw-h-[600px] tw-w-[300px] tw-shadow-xl">
            <div className="tw-rounded-[2rem] tw-overflow-hidden tw-w-full tw-h-full tw-bg-black tw-relative tw-text-white">
              {backgroundComponent(displayTracks, "tw-blur-[50px]")}

              {/* Progress bar */}
              <div
                className="tw-absolute tw-bottom-0 tw-left-4 tw-right-4 tw-h-1 tw-bg-white/20 tw-z-40 tw-cursor-pointer tw-rounded-full"
                onClick={handleProgressClick}
              >
                <div
                  className="tw-h-full tw-bg-white tw-transition-all tw-duration-100 tw-rounded-full"
                  style={{ width: `${progress}%` }}
                />
              </div>

              {/* Playback overlay */}
              <div
                onClick={togglePlayback}
                className={`tw-absolute tw-inset-0 tw-z-30 tw-flex tw-items-center tw-justify-center tw-cursor-pointer tw-transition-all tw-duration-300 ${
                  !isPlaying ? "tw-bg-black/40" : "tw-bg-transparent"
                }`}
              >
                <FaPlay
                  size={48}
                  className={`tw-text-white tw-ml-2 tw-transition-opacity tw-duration-300 ${
                    !isPlaying ? "tw-opacity-100" : "tw-opacity-0"
                  }`}
                />
              </div>

              {/* Header */}

              <div className="tw-text-center tw-flex tw-items-center tw-justify-center tw-mb-4 tw-z-20 tw-absolute tw-w-full tw-top-8">
                <JammableLogo />
              </div>

              {/* Cover Arts */}
              <div className="tw-w-full tw-z-20 tw-h-full tw-flex tw-flex-col tw-items-start  tw-justify-center tw-gap-20 tw-p-4">
                {/* Vocal Track */}
                {trackComponent(displayTracks.vocal, true)}

                {/* Instrumental Track */}
                {trackComponent(displayTracks.instrumental, false)}
              </div>

              {/* Mashup Info & CTA */}
              <div className="tw-flex-shrink-0 tw-text-start tw-mt-4 tw-z-20 tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-p-4">
                <div className="tw-absolute tw-top-0 tw-left-0 tw-w-full tw-h-full tw-bg-gradient-to-t tw-from-black/60 tw-to-transparent tw-z-10" />
                <div className="tw-z-20">
                  <Link
                    href={`/@${displayData.user.username}`}
                    className="tw-flex tw-items-center tw-justify-start tw-gap-1 tw-mb-2 tw-z-20"
                  >
                    <Image
                      src={displayData.user.avatar}
                      alt={displayData.user.username}
                      width={30}
                      height={30}
                      className="tw-rounded-lg tw-z-20"
                    />
                    <div className="tw-z-20">
                      <div className="tw-text-xs  tw-font-bold">
                        @{displayData.user.username}
                      </div>
                      <div className="tw-text-[9px] tw-text-white/50 tw-font-semibold">
                        {dayjs(displayData.createdAt).fromNow()}
                      </div>
                    </div>
                  </Link>

                  <div className="tw-font-bold tw-text-base  tw-text-sm tw-z-20">
                    {displayData.caption}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="tw-text-white tw-flex  tw-justify-center tw-w-full tw-text-2xl tw-font-bold  tw-p-4">
          <div className="tw-flex tw-flex-col tw-relative tw-max-w-sm tw-bg-white/10 tw-z-20  tw-shadow-xl tw-p-6 tw-rounded-4xl tw-items-center tw-justify-center">
            <div className="tw-flex tw-flex-col tw-gap-2 tw-items-center tw-justify-center tw-w-[300px]">
              <Button
                onClick={handleAppStoreClick}
                className="tw-uppercase tw-flex tw-font-black tw-items-center tw-justify-center tw-rounded-2xl tw-bg-white tw-text-black hover:tw-opacity-90 tw-transition tw-duration-150 tw-flex tw-gap-1 md:tw-px-6 md:tw-pr-8 tw-h-14 tw-py-0 tw-border-none hover:tw-bg-white tw-w-full"
              >
                <FaApple size={30} />
                <div className="tw-flex tw-flex-col tw-items-start tw-justify-center">
                  <div className="tw-text-xs tw-font-semibold tw--mb-0.5">
                    Get it on
                  </div>
                  <div className="tw-flex tw-gap-1 tw-text-base tw-items-center tw-justify-center tw-font-black">
                    App Store
                  </div>
                </div>
              </Button>

              <Button
                onClick={handlePlayStoreClick}
                className="tw-uppercase tw-font-black tw-items-center tw-justify-center tw-rounded-2xl tw-bg-[#34A853] tw-text-white hover:tw-bg-[#2C8C46] tw-transition tw-duration-150 tw-flex tw-gap-2 tw-px-6 tw-h-14 tw-border-none tw-w-full"
              >
                <FaGooglePlay size={30} color="#ffffff" />
                <div className="tw-flex tw-flex-col tw-items-start tw-justify-center">
                  <div className="tw-text-xs tw-font-semibold tw--mb-0.5">
                    Get it on
                  </div>
                  <div className="tw-flex tw-gap-1 tw-text-base tw-items-center tw-justify-center tw-font-black">
                    Play Store
                  </div>
                </div>
              </Button>

              <Button
                onClick={handleDiscordClick}
                className="tw-uppercase  tw-border-none tw-font-black tw-items-center tw-justify-center tw-rounded-2xl tw-bg-[#5865F2] hover:tw-bg-[#4752C4] tw-transition tw-duration-150 tw-flex tw-gap-2 tw-px-6 tw-h-14 tw-w-full"
              >
                <FaDiscord size={30} />
                <div className="tw-flex tw-flex-col tw-items-start tw-justify-center">
                  <div className="tw-text-xs tw-font-semibold tw--mb-0.5">
                    10K+ members
                  </div>
                  <div className="tw-flex tw-gap-1 tw-text-base tw-items-center tw-justify-center tw-font-black">
                    Join Discord
                  </div>
                </div>
              </Button>
            </div>
            <div className="tw-text-base tw-text-left k tw-mt-4 tw-text-center tw-opacity-50">
              Make your own mashups, like{" "}
              <span className="tw-font-bold tw-inline-flex tw-items-center tw-gap-1">
                <Image
                  src={displayData.user.avatar}
                  alt={displayData.user.username}
                  width={20}
                  height={20}
                  className="tw-rounded-full"
                />
                {displayData.user.username}!
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MashupPage;

export async function getServerSideProps(context) {
  const { mashupId } = context.params;

  try {
    const db = await connectToDb();

    if (!ObjectId.isValid(mashupId)) {
      return { props: { mashupData: null } };
    }

    // Use aggregation to fetch mashup and its tracks
    const pipeline = [
      {
        $match: {
          _id: new ObjectId(mashupId),
        },
      },
      {
        $lookup: {
          from: "mashups",
          localField: "mashupId",
          foreignField: "_id",
          as: "mashup",
        },
      },
      {
        $unwind: { path: "$mashup", preserveNullAndEmptyArrays: true },
      },
      {
        $addFields: {
          mashupRecipe: {
            $cond: [
              { $ifNull: ["$mashup", false] },
              "$mashup.mashupRecipe",
              "$mashupRecipe",
            ],
          },
        },
      },
      {
        $lookup: {
          from: "audiofiles",
          localField: "mashupRecipe.trackIds",
          foreignField: "_id",
          as: "tracks",
        },
      },

      {
        // Only the byline. Without this $project the page props carried the
        // poster's whole user record (email, IPs, Stripe customer, push
        // token) to anyone who opened the post.
        $lookup: {
          from: "users",
          localField: "userId",
          foreignField: "firebaseUid",
          as: "user",
          pipeline: [{ $project: { _id: 1, username: 1, avatar: 1 } }],
        },
      },
      {
        $unwind: "$user",
      },
      { $project: { userId: 0 } },
    ];

    const results = await db.connection.db
      .collection("posts")
      .aggregate(pipeline)
      .toArray();

    const mashup = results[0];

    if (!mashup) {
      return { props: { mashupData: null, ogImageUrl: null } };
    }

    const { mashupRecipe, tracks } = mashup;
    const vocalTrackIdObj =
      mashupRecipe?.recordingSession?.initialState?.activeVocalTrackId ||
      mashupRecipe?.recordingSession?.alignmentChanges?.[0]?.alignment
        ?.vocalTrackId;
    const instrumentalTrackIdObj =
      mashupRecipe?.recordingSession?.initialState?.activeInstrumentalTrackId ||
      mashupRecipe?.recordingSession?.alignmentChanges?.[0]?.alignment
        ?.instrumentalTrackId;

    const vocalTrackId = vocalTrackIdObj?.$oid || vocalTrackIdObj;
    const instrumentalTrackId =
      instrumentalTrackIdObj?.$oid || instrumentalTrackIdObj;

    const vocalTrack = tracks?.find(
      (t) => String(t._id) === String(vocalTrackId),
    );
    const instrumentalTrack = tracks?.find(
      (t) => String(t._id) === String(instrumentalTrackId),
    );

    // Extract dominant colors from album art (in parallel)
    const [vocalColor, instrumentalColor] = await Promise.all([
      extractDominantColor(vocalTrack?.metadata?.image?.url),
      extractDominantColor(instrumentalTrack?.metadata?.image?.url),
    ]);

    const ogParams = new URLSearchParams();
    if (vocalTrack?.metadata?.image?.url) {
      ogParams.set("vocalImage", vocalTrack.metadata.image.url);
    }
    if (instrumentalTrack?.metadata?.image?.url) {
      ogParams.set("instrumentalImage", instrumentalTrack.metadata.image.url);
    }
    if (vocalTrack?.metadata?.title) {
      ogParams.set("vocalTitle", vocalTrack.metadata.title);
    }
    if (instrumentalTrack?.metadata?.title) {
      ogParams.set("instrumentalTitle", instrumentalTrack.metadata.title);
    }
    if (vocalColor) {
      ogParams.set("vocalColor", vocalColor);
    }
    if (instrumentalColor) {
      ogParams.set("instrumentalColor", instrumentalColor);
    }
    if (mashup.user?.username) {
      ogParams.set("username", mashup.user.username);
    }
    const ogImageUrl = `https://jammable.com/api/og/mashup/${mashupId}?${ogParams.toString()}`;

    return {
      props: {
        mashupData: JSON.parse(JSON.stringify(mashup)),
        ogImageUrl,
      },
    };
  } catch (error) {
    console.error("Failed to fetch mashup data:", error);
    return {
      props: { mashupData: null, ogImageUrl: null },
    };
  }
}
